import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Info } from "lucide-react";

import useCandidateProfileData from "./hooks/useCandidateProfileData";
import {
  validateCertifications,
  validateEducations,
  validateSoftSkillsEvidence,
} from "./utils/profileFieldNormalization";
import CareerDetailsSection from "./sections/CareerDetailsSection";
import ChooseRolesSection from "./sections/ChooseRolesSection";
import "./CandidateProfileEdit.css";

const PersonIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const DescriptionIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const AppsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
  </svg>
);

const STEPS = [
  {
    key: "careerDetails",
    title: "Enter career details",
    sub: "candidate professional details",
    icon: <DescriptionIcon />,
    breadcrumb: "Career Details",
  },
  {
    key: "chooseRoles",
    title: "Soft-skill and languages",
    sub: "soft-skill and languages",
    icon: <AppsIcon />,
    breadcrumb: "Role Selection",
  },
];

/**
 * Shared profile edit form — used by full page and career journey dialog.
 */
const CandidateProfileEditContent = ({ userId, variant = "page", onSaved }) => {
  const { profileData, loading, saving, error, fetchProfile, saveProfile } =
    useCandidateProfileData();

  const [activeStep, setActiveStep] = useState(0);
  const [localEdits, setLocalEdits] = useState({
    completeProfile: null,
    careerDetails: null,
    chooseRoles: null,
  });
  const [chooseRolesValidationKey, setChooseRolesValidationKey] = useState(0);

  useEffect(() => {
    if (userId) fetchProfile(userId);
  }, [userId, fetchProfile]);

  const handleSave = async () => {
    const sectionsToSave = Object.entries(localEdits).filter(
      ([, data]) => data !== null,
    );

    if (sectionsToSave.length === 0) {
      toast("No changes to save.", {
        icon: <Info size={18} style={{ color: "#3b82f6" }} />,
      });
      return;
    }

    try {
      for (const [sectionKey, sectionData] of sectionsToSave) {
        if (sectionKey === "chooseRoles") {
          const softSkillCheck = validateSoftSkillsEvidence(sectionData?.softSkills);
          if (!softSkillCheck.valid) {
            setChooseRolesValidationKey((key) => key + 1);
            setActiveStep(STEPS.findIndex((s) => s.key === "chooseRoles"));
            toast.error(softSkillCheck.message);
            return;
          }
        }
        if (sectionKey === "careerDetails") {
          const certCheck = validateCertifications(sectionData?.certifications);
          if (!certCheck.valid) {
            setActiveStep(STEPS.findIndex((s) => s.key === "careerDetails"));
            toast.error(certCheck.message);
            return;
          }
          const eduCheck = validateEducations(sectionData?.educations);
          if (!eduCheck.valid) {
            setActiveStep(STEPS.findIndex((s) => s.key === "careerDetails"));
            toast.error(eduCheck.message);
            return;
          }
        }
        await saveProfile(userId, sectionKey, sectionData);
      }
      setLocalEdits({
        completeProfile: null,
        careerDetails: null,
        chooseRoles: null,
      });
      toast.success("Profile saved successfully!");
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Failed to save.");
    }
  };

  const candidateName = profileData
    ? `${profileData?.personalInfo?.first_name || ""} ${profileData?.personalInfo?.last_name || ""}`.trim() ||
    "Candidate"
    : "Candidate";

  const candidateEmail =
    profileData?.personalInfo?.email || profileData?.careerDetails?.email || "";

  const initials =
    candidateName
      .split(" ")
      .filter(Boolean)
      .map((namePart) => namePart[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const layoutClass =
    variant === "dialog" ? "cpe-layout cpe-layout-dialog" : "cpe-layout";

  return (
    <div className={layoutClass}>
      <div className="cpe-sidebar">
        <div className="cpe-sidebar-track" />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "0 8px 20px 12px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div className="cpe-avatar" style={{ width: 38, height: 38, fontSize: 14 }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
              {candidateName}
            </div>
            {candidateEmail && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>{candidateEmail}</div>
            )}
          </div>
        </div>

        {STEPS.map((step, stepIndex) => (
          <div
            key={step.key}
            className={`cpe-step${activeStep === stepIndex ? " active" : ""}`}
            onClick={() => setActiveStep(stepIndex)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                setActiveStep(stepIndex);
              }
            }}
          >
            <div className="cpe-step-header">
              <span className="cpe-step-icon">{step.icon}</span>
              <span className="cpe-step-title">{step.title}</span>
            </div>
            <div className="cpe-step-sub">{step.sub}</div>
          </div>
        ))}
      </div>

      <div className="cpe-content">
        <div className="cpe-breadcrumb">
          {STEPS.map((step, stepIndex) => (
            <React.Fragment key={step.key}>
              <span
                className={`cpe-breadcrumb-step${activeStep === stepIndex ? " active" : ""}`}
              >
                {step.breadcrumb}
              </span>
              {stepIndex < STEPS.length - 1 && (
                <span className="cpe-breadcrumb-arrow">→</span>
              )}
            </React.Fragment>
          ))}
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1f2937", margin: "0 0 6px" }}>
          {STEPS[activeStep]?.key === "careerDetails" && "Career details"}
          {STEPS[activeStep]?.key === "chooseRoles" && "Preferred roles & skills"}
        </h2>
        <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 24px" }}>
          {STEPS[activeStep]?.key === "careerDetails" && "CV, LinkedIn and employment background"}
          {STEPS[activeStep]?.key === "chooseRoles" && "Target roles, technical skills, and attributes"}
        </p>

        {error && (
          <div className="cpe-alert-error" style={{ marginBottom: 20 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="cpe-loading">
            <div
              style={{
                width: 28,
                height: 28,
                border: "3px solid #e5e7eb",
                borderTop: "3px solid #6c5ce7",
                borderRadius: "50%",
                animation: "cpe-spin 0.8s linear infinite",
                flexShrink: 0,
              }}
            />
            Loading candidate profile…
          </div>
        ) : (
          <>
            <div style={{ display: STEPS[activeStep]?.key === "careerDetails" ? "block" : "none" }}>
              <CareerDetailsSection
                profileData={profileData}
                userId={userId}
                onChange={(data) =>
                  setLocalEdits((prev) => ({ ...prev, careerDetails: data }))
                }
              />
            </div>
            <div style={{ display: STEPS[activeStep]?.key === "chooseRoles" ? "block" : "none" }}>
              <ChooseRolesSection
                profileData={profileData}
                validationKey={chooseRolesValidationKey}
                onChange={(data) =>
                  setLocalEdits((prev) => ({ ...prev, chooseRoles: data }))
                }
              />
            </div>

            <div className="cpe-save-bar">
              <span className="cpe-save-bar-note" />
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
                disabled={activeStep === 0}
              >
                Previous
              </button>
              {activeStep < STEPS.length - 1 && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setActiveStep((prev) => Math.min(STEPS.length - 1, prev + 1))}
                >
                  Next →
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CandidateProfileEditContent;
